import React from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { formatPhoneNumber } from "@/lib/format-utils";
import { StatusBadge } from "@/components/ui/status-badge";
import { StatusBadge as TaskStatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import {
  User,
  Home,
  MessageSquare,
  CheckSquare,
  FolderKanban,
  DollarSign,
  Calendar,
  Mail,
  Phone,
  MapPin,
  Clock,
  UserPlus,
  Check,
  ExternalLink,
  ArrowRight,
  Zap,
} from "lucide-react";
import type { EnhancedSearchResult } from "@/hooks/useEnhancedSearch";
import { contributionStatusMap, projectStatusMap, requestStatusMap, taskStatusMap } from "@/lib/status-mapping";
import { format, formatDistanceToNow } from "date-fns";
import { STALE_TIME_30_SECONDS, STALE_TIME_1_MINUTE, STALE_TIME_5_MINUTES, STALE_TIME_1_HOUR, STALE_TIME_24_HOURS } from "@/lib/query-constants";
import { parseLocalDate, safeFormatDate, parseEventDateTime, timeZoneDateYmd } from "@/lib/date-utils";
import { isSupabaseUnavailableError } from "@/lib/supabase-compat";
import { fetchTaskPreview, fetchContributionPreview } from "./search-preview-queries";
import { useOrganization } from "@/hooks/useOrganization";
import { useOrgSettings } from "@/hooks/useOrgSettings";

interface SearchPreviewPanelProps {
  result: EnhancedSearchResult | null;
  onAction?: (action: string) => void;
  onSelect?: () => void;
}

const statusMaps: Record<string, Record<string, { variant?: string; label: string }>> = {
  request: requestStatusMap,
  task: taskStatusMap,
  project: projectStatusMap,
  contribution: contributionStatusMap,
};

const normalizeRequestRow = (row: Record<string, unknown>) => ({
  ...row,
  title: (row.summary as string | undefined) || "Request",
  description: (row.details as string | undefined) ?? null,
});

export function SearchPreviewPanel({ result, onAction, onSelect }: SearchPreviewPanelProps) {
  if (!result) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
        <div className="text-center">
          <Zap className="h-8 w-8 mx-auto mb-2 opacity-30" />
          <p>Select an item to preview</p>
          <p className="text-xs mt-1 opacity-70">Use arrow keys to navigate</p>
        </div>
      </div>
    );
  }

  // Show command preview
  if (result.type === "command") {
    return <CommandPreview result={result} onSelect={onSelect} />;
  }

  // Show record preview based on type
  switch (result.objectType) {
    case "person":
      return <PersonPreview result={result} onAction={onAction} onSelect={onSelect} />;
    case "household":
      return <HouseholdPreview result={result} onAction={onAction} onSelect={onSelect} />;
    case "task":
      return <TaskPreview result={result} onAction={onAction} onSelect={onSelect} />;
    case "request":
      return <RequestPreview result={result} onAction={onAction} onSelect={onSelect} />;
    case "contribution":
      return <ContributionPreview result={result} onSelect={onSelect} />;
    case "event":
      return <EventPreview result={result} onSelect={onSelect} />;
    default:
      return <GenericPreview result={result} onSelect={onSelect} />;
  }
}

// Command preview
function CommandPreview({ result, onSelect }: { result: EnhancedSearchResult; onSelect?: () => void }) {
  const categoryLabels: Record<string, { label: string; description: string; actionLabel: string }> = {
    navigation: { label: "Navigation", description: "Go to this page", actionLabel: "Open" },
    create: { label: "Quick Create", description: "Create a new record", actionLabel: "Execute" },
    action: { label: "Quick Action", description: "Run this action immediately", actionLabel: "Execute" },
    settings: { label: "Settings", description: "Open settings", actionLabel: "Open" },
    help: { label: "Help", description: "Get help", actionLabel: "Open" },
  };

  const meta = categoryLabels[result.category] || { label: "Command", description: "Execute command", actionLabel: "Execute" };

  return (
    <div className="p-5 h-full flex items-center justify-center">
      <div className="w-full max-w-[220px] text-center flex flex-col items-center gap-4">
        <div className="h-14 w-14 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center">
          <Zap className="h-7 w-7 text-primary" />
        </div>

        <div className="space-y-1">
          <p className="text-[11px] uppercase tracking-[0.1em] text-muted-foreground font-semibold">
            {meta.label}
          </p>
          <h3 className="text-base font-semibold leading-tight">{result.label}</h3>
        </div>

        <p className="text-sm text-muted-foreground leading-snug">
          {result.description || meta.description}
        </p>

        <Button onClick={onSelect} className="w-full gap-2">
          {meta.actionLabel}
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

// Person preview with details fetch
function PersonPreview({ result, onAction, onSelect }: { result: EnhancedSearchResult; onAction?: (action: string) => void; onSelect?: () => void }) {
  const { data: person, isLoading } = useQuery({
    queryKey: ["person-preview", result.objectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("people")
        .select(`
          *,
          household:households(name)
        `)
        .eq("id", result.objectId)
        .single();
      if (error) {
        if (isSupabaseUnavailableError(error)) return null;
        throw error;
      }
      return data;
    },
    enabled: !!result.objectId,
    staleTime: STALE_TIME_30_SECONDS,
  });

  if (isLoading) return <PreviewSkeleton />;

  const initials = result.label
    .split(" ")
    .map(n => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="p-4 h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <Avatar className="h-14 w-14">
          <AvatarImage src={person?.photo_url ?? undefined} />
          <AvatarFallback className="bg-primary/10 text-primary text-lg">
            {initials}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold truncate">{result.label}</h3>
          {person?.household?.name && (
            <p className="text-sm text-muted-foreground truncate">
              {person.household.name}
            </p>
          )}
        </div>
      </div>

      {/* Details */}
      <div className="space-y-2 flex-1">
        {person?.email && (
          <div className="flex items-center gap-2 text-sm">
            <Mail className="h-4 w-4 text-muted-foreground" />
            <span className="truncate">{person.email}</span>
          </div>
        )}
        {person?.phone && (
          <div className="flex items-center gap-2 text-sm">
            <Phone className="h-4 w-4 text-muted-foreground" />
            <span>{formatPhoneNumber(person.phone)}</span>
          </div>
        )}
        {(person?.city || person?.state) && (
          <div className="flex items-center gap-2 text-sm">
            <MapPin className="h-4 w-4 text-muted-foreground" />
            <span>{[person.city, person.state].filter(Boolean).join(", ")}</span>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="mt-4 flex gap-2">
        <Button variant="outline" size="sm" className="flex-1 gap-1" onClick={() => onAction?.("message")}>
          <MessageSquare className="h-3.5 w-3.5" />
          Message
        </Button>
        <Button size="sm" className="flex-1 gap-1" onClick={onSelect}>
          View Profile
          <ExternalLink className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

// Household preview
function HouseholdPreview({ result, onAction, onSelect }: { result: EnhancedSearchResult; onAction?: (action: string) => void; onSelect?: () => void }) {
  const { data: household, isLoading } = useQuery({
    queryKey: ["household-preview", result.objectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("households")
        .select(`
          *,
          members:people(id, first_name, last_name)
        `)
        .eq("id", result.objectId)
        .single();
      if (error) {
        if (isSupabaseUnavailableError(error)) return null;
        throw error;
      }
      return data;
    },
    enabled: !!result.objectId,
    staleTime: STALE_TIME_30_SECONDS,
  });

  if (isLoading) return <PreviewSkeleton />;

  return (
    <div className="p-4 h-full flex flex-col">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-12 w-12 rounded-xl bg-accent-purple-500/10 flex items-center justify-center">
          <Home className="h-6 w-6 text-accent-purple-500" />
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
            Household
          </p>
          <h3 className="text-lg font-semibold">{result.label}</h3>
        </div>
      </div>

      {/* Address */}
      {household?.address && (
        <div className="flex items-start gap-2 text-sm mb-3">
          <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
          <span className="text-muted-foreground">{household.address}</span>
        </div>
      )}

      {/* Members */}
      {household?.members && household.members.length > 0 && (
        <div className="flex-1">
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-2">
            Members ({household.members.length})
          </p>
          <div className="space-y-1">
            {household.members.slice(0, 4).map((member: { id: string; first_name: string; last_name: string }) => (
              <div key={member.id} className="flex items-center gap-2 text-sm">
                <User className="h-3.5 w-3.5 text-muted-foreground" />
                <span>{member.first_name} {member.last_name}</span>
              </div>
            ))}
            {household.members.length > 4 && (
              <p className="text-xs text-muted-foreground">
                +{household.members.length - 4} more
              </p>
            )}
          </div>
        </div>
      )}

      <div className="mt-4">
        <Button size="sm" className="w-full gap-1" onClick={onSelect}>
          View Household
          <ExternalLink className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

// Task preview
function TaskPreview({ result, onAction, onSelect }: { result: EnhancedSearchResult; onAction?: (action: string) => void; onSelect?: () => void }) {
  const { organizationId } = useOrganization();
  const { data: task, isLoading } = useQuery({
    queryKey: ["task-preview", result.objectId, organizationId],
    queryFn: () => fetchTaskPreview(result.objectId!, organizationId!),
    enabled: !!result.objectId && !!organizationId,
    staleTime: STALE_TIME_30_SECONDS,
  });

  if (isLoading) return <PreviewSkeleton />;

  const statusInfo = result.status && taskStatusMap[result.status as keyof typeof taskStatusMap];
  const isComplete = result.status === "done" || result.status === "completed";

  return (
    <div className="p-4 h-full flex flex-col">
      <div className="flex items-start gap-3 mb-4">
        <div className="h-12 w-12 rounded-xl bg-warning-500/10 flex items-center justify-center flex-shrink-0">
          <CheckSquare className="h-6 w-6 text-warning-500" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
              Task
            </p>
            {statusInfo && (
              <TaskStatusBadge status={result.status} className="text-xs" />
            )}
          </div>
          <h3 className="text-lg font-semibold truncate">{result.label}</h3>
        </div>
      </div>

      {/* Details */}
      <div className="space-y-2 flex-1">
        {task?.description && (
          <p className="text-sm text-muted-foreground line-clamp-3">{task.description}</p>
        )}
        {task?.assignee && (
          <div className="flex items-center gap-2 text-sm">
            <User className="h-4 w-4 text-muted-foreground" />
            <span>Assigned to {task.assignee.first_name} {task.assignee.last_name}</span>
          </div>
        )}
        {task?.due_date && (
          <div className="flex items-center gap-2 text-sm">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span>Due {format((parseLocalDate(task.due_date) ?? new Date(task.due_date)), "MMM d, yyyy")}</span>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="mt-4 flex gap-2">
        {!isComplete && (
          <Button variant="outline" size="sm" className="flex-1 gap-1" onClick={() => onAction?.("complete")}>
            <Check className="h-3.5 w-3.5" />
            Complete
          </Button>
        )}
        <Button size="sm" className={cn("gap-1", isComplete ? "w-full" : "flex-1")} onClick={onSelect}>
          View Task
          <ExternalLink className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

// Request preview
function RequestPreview({ result, onAction, onSelect }: { result: EnhancedSearchResult; onAction?: (action: string) => void; onSelect?: () => void }) {
  const { data: request, isLoading } = useQuery({
    queryKey: ["request-preview", result.objectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("requests")
        .select(`
          *,
          requester:people!requests_requester_person_id_fkey(first_name, last_name),
          assignee:people!requests_triage_owner_id_fkey(first_name, last_name)
        `)
        .eq("id", result.objectId)
        .single();

      if (error) {
        if (isSupabaseUnavailableError(error)) return null;
        throw error;
      }

      return normalizeRequestRow(data as Record<string, unknown>);
    },
    enabled: !!result.objectId,
    staleTime: STALE_TIME_30_SECONDS,
  });

  if (isLoading) return <PreviewSkeleton />;

  const statusInfo = result.status && requestStatusMap[result.status as keyof typeof requestStatusMap];

  return (
    <div className="p-4 h-full flex flex-col">
      <div className="flex items-start gap-3 mb-4">
        <div className="h-12 w-12 rounded-xl bg-error-500/10 flex items-center justify-center flex-shrink-0">
          <MessageSquare className="h-6 w-6 text-error-500" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
              Care Request
            </p>
            {statusInfo && (
              <StatusBadge
                variant={statusInfo.variant as "error" | "info" | "neutral" | "success" | "warning"}
                label={statusInfo.label}
                className="text-xs"
              />
            )}
          </div>
          <h3 className="text-lg font-semibold truncate">{result.label}</h3>
        </div>
      </div>

      {/* Details */}
      <div className="space-y-2 flex-1">
        {request?.description && (
          <p className="text-sm text-muted-foreground line-clamp-3">{request.description}</p>
        )}
        {request?.requester && (
          <div className="flex items-center gap-2 text-sm">
            <User className="h-4 w-4 text-muted-foreground" />
            <span>From {request.requester?.first_name ?? ""} {request.requester?.last_name ?? ""}</span>
          </div>
        )}
        {request?.assignee && (
          <div className="flex items-center gap-2 text-sm">
            <UserPlus className="h-4 w-4 text-muted-foreground" />
            <span>Assigned to {request.assignee?.first_name ?? ""} {request.assignee?.last_name ?? ""}</span>
          </div>
        )}
        {request?.created_at && (
          <div className="flex items-center gap-2 text-sm">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span>Created {formatDistanceToNow(new Date(request.created_at), { addSuffix: true })}</span>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="mt-4 flex gap-2">
        <Button variant="outline" size="sm" className="flex-1 gap-1" onClick={() => onAction?.("assign")}>
          <UserPlus className="h-3.5 w-3.5" />
          Assign
        </Button>
        <Button size="sm" className="flex-1 gap-1" onClick={onSelect}>
          View Request
          <ExternalLink className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

// Contribution preview
function ContributionPreview({ result, onSelect }: { result: EnhancedSearchResult; onSelect?: () => void }) {
  const { organizationId } = useOrganization();
  const { data: contribution, isLoading } = useQuery({
    queryKey: ["contribution-preview", result.objectId, organizationId],
    queryFn: () => fetchContributionPreview(result.objectId!, organizationId!),
    enabled: !!result.objectId && !!organizationId,
    staleTime: STALE_TIME_30_SECONDS,
  });

  if (isLoading) return <PreviewSkeleton />;

  return (
    <div className="p-4 h-full flex flex-col">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-12 w-12 rounded-xl bg-success-500/10 flex items-center justify-center">
          <DollarSign className="h-6 w-6 text-success-500" />
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
            Contribution
          </p>
          <h3 className="text-lg font-semibold">{result.label}</h3>
        </div>
      </div>

      <div className="space-y-2 flex-1">
        {contribution?.person && (
          <div className="flex items-center gap-2 text-sm">
            <User className="h-4 w-4 text-muted-foreground" />
            <span>{contribution.person?.first_name ?? ""} {contribution.person?.last_name ?? ""}</span>
          </div>
        )}
        {!!contribution?.allocations?.length && (
          <div className="flex items-center gap-2 text-sm">
            <FolderKanban className="h-4 w-4 text-muted-foreground" />
            <span>{[...new Set(contribution.allocations.map(allocation => allocation.fund?.name).filter(Boolean))].join(", ")}</span>
          </div>
        )}
        <div className="flex items-center gap-2 text-sm">
          <Calendar className="h-4 w-4 text-muted-foreground" />
          <span>{safeFormatDate(contribution?.date, "MMM d, yyyy", "Unknown Date")}</span>
        </div>
      </div>

      <div className="mt-4">
        <Button size="sm" className="w-full gap-1" onClick={onSelect}>
          View Details
          <ExternalLink className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

// Event preview
function EventPreview({ result, onSelect }: { result: EnhancedSearchResult; onSelect?: () => void }) {
  const { settings: orgSettings } = useOrgSettings();
  const { data: event, isLoading } = useQuery({
    queryKey: ["event-preview", result.objectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("events")
        .select("*")
        .eq("id", result.objectId)
        .single();
      if (error) {
        if (isSupabaseUnavailableError(error)) return null;
        throw error;
      }
      return data;
    },
    enabled: !!result.objectId,
    staleTime: STALE_TIME_30_SECONDS,
  });

  if (isLoading) return <PreviewSkeleton />;

  return (
    <div className="p-4 h-full flex flex-col">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-12 w-12 rounded-xl bg-accent-purple-500/10 flex items-center justify-center">
          <Calendar className="h-6 w-6 text-accent-purple-500" />
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
            Event
          </p>
          <h3 className="text-lg font-semibold">{result.label}</h3>
        </div>
      </div>

      <div className="space-y-2 flex-1">
        {event?.description && (
          <p className="text-sm text-muted-foreground line-clamp-3">{event.description}</p>
        )}
        {event?.start_time && (
          <div className="flex items-center gap-2 text-sm">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span>
              {event.all_day
                ? (() => {
                    // An all-day event's start_time is anchored to
                    // organization midnight — format its canonical
                    // calendar date rather than the browser timezone of
                    // whoever opened this search preview (Codex round-15
                    // finding on #5782). Show the full range for a
                    // multi-day all-day event instead of only its start
                    // date (Codex round-18 finding on #5782).
                    const startInstant = parseEventDateTime(event.start_time);
                    if (!startInstant) return "—";
                    const [sy, sm, sd] = (event.all_day_date ?? timeZoneDateYmd(orgSettings.timezone, startInstant))
                      .split("-")
                      .map(Number);
                    const startDate = new Date(sy, sm - 1, sd);
                    const endInstant = event.end_time ? parseEventDateTime(event.end_time) : null;
                    if (!endInstant) return format(startDate, "MMM d, yyyy") + " • All day";
                    const [ey, em, ed] = (event.all_day_end_date ?? timeZoneDateYmd(orgSettings.timezone, endInstant))
                      .split("-")
                      .map(Number);
                    const endDate = new Date(ey, em - 1, ed);
                    const dateLabel = startDate.toDateString() === endDate.toDateString()
                      ? format(startDate, "MMM d, yyyy")
                      : `${format(startDate, "MMM d, yyyy")} - ${format(endDate, "MMM d, yyyy")}`;
                    return `${dateLabel} • All day`;
                  })()
                : format(parseEventDateTime(event.start_time) || new Date(), "MMM d, yyyy 'at' h:mm a")}
            </span>
          </div>
        )}
        {event?.location && (
          <div className="flex items-center gap-2 text-sm">
            <MapPin className="h-4 w-4 text-muted-foreground" />
            <span>{event.location}</span>
          </div>
        )}
      </div>

      <div className="mt-4">
        <Button size="sm" className="w-full gap-1" onClick={onSelect}>
          View Event
          <ExternalLink className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

// Generic preview for other types
function GenericPreview({ result, onSelect }: { result: EnhancedSearchResult; onSelect?: () => void }) {
  const typeLabels: Record<string, string> = {
    project: "Project",
    fund: "Fund",
    asset: "Asset",
    campaign: "Campaign",
    pledge: "Pledge",
  };

  return (
    <div className="p-4 h-full flex flex-col">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-12 w-12 rounded-xl bg-muted flex items-center justify-center">
          <FolderKanban className="h-6 w-6 text-muted-foreground" />
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
            {typeLabels[result.objectType || ""] || "Record"}
          </p>
          <h3 className="text-lg font-semibold">{result.label}</h3>
        </div>
      </div>

      {result.description && (
        <p className="text-sm text-muted-foreground mb-4">{result.description}</p>
      )}

      <div className="mt-auto">
        <Button size="sm" className="w-full gap-1" onClick={onSelect}>
          View Details
          <ExternalLink className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

// Loading skeleton
function PreviewSkeleton() {
  return (
    <div className="p-4 h-full">
      <div className="flex items-center gap-3 mb-4">
        <Skeleton className="h-12 w-12 rounded-xl" />
        <div className="space-y-2">
          <Skeleton className="h-3 w-16" />
          <Skeleton className="h-5 w-32" />
        </div>
      </div>
      <div className="space-y-2">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    </div>
  );
}
